import type { AgentState } from './agent-api.js';
import type { AgentChannel } from './agent-releases.js';
import type { GameId } from './games.js';

export const SERVER_STATUSES = [
  'PROVISIONING',
  'STOPPED',
  'STARTING',
  'RUNNING',
  'STOPPING',
  'ARCHIVING',
  'ARCHIVED',
  'RESTORING',
  'DESTROYING', // the destroy workflow is removing its instance and data volume
  'DESTROYED', // gone, except its backups; the record is kept for history (and to find the backups)
  'FAILED',
] as const;

export type ServerStatus = (typeof SERVER_STATUSES)[number];

/** EC2 instance states, as reported by EC2 state-change events. */
export const INSTANCE_STATES = ['pending', 'running', 'stopping', 'stopped', 'shutting-down', 'terminated'] as const;

export type InstanceState = (typeof INSTANCE_STATES)[number];

/**
 * The instance states state sync receives (the EventBridge rule filters on these). The in-between
 * states add nothing that `status` doesn't already say.
 */
export const SYNCED_INSTANCE_STATES = ['running', 'stopped', 'terminated'] as const satisfies readonly InstanceState[];

export function isInstanceState(value: unknown): value is InstanceState {
  return typeof value === 'string' && (INSTANCE_STATES as readonly string[]).includes(value);
}

/** Idle stop for a server that hasn't chosen: 30 minutes with nobody playing. */
export const DEFAULT_IDLE_STOP_MINUTES = 30;
/** The longest idle limit a server can choose: a day. */
export const MAX_IDLE_STOP_MINUTES = 24 * 60;

/**
 * Instance types an admin may put a server on (performance tests, M10.6; sizes come with M10.7):
 * Graviton only, as the launch template's image is arm64, and nothing past 2xlarge.
 */
export const INSTANCE_FAMILIES = ['t4g', 'c7g', 'm7g', 'r7g', 'c8g', 'm8g', 'r8g'] as const;
export const INSTANCE_TYPE = new RegExp(`^(${INSTANCE_FAMILIES.join('|')})\\.(medium|large|xlarge|2xlarge)$`);

/** How big a server is (M10.7): what it runs on, and how many may play at once. */
export const SERVER_SIZES = ['small', 'medium', 'large'] as const;
export type ServerSize = (typeof SERVER_SIZES)[number];
/** Servers that never chose a size (all of them before M10.7) are Small. */
export const DEFAULT_SERVER_SIZE: ServerSize = 'small';

/**
 * Each size's instance; no player limit (`players` is only what the choice says). The owner's
 * baseline from the first performance run (2026-10-21, docs/perf/2026-10-21-tiers): Small passed
 * the strict bar; Medium and Large stayed playable exploring. The budget run (2026-10-22) found
 * nothing cheaper worth it.
 */
export const SIZE_SPECS: Readonly<Record<ServerSize, { name: string; players: string; instanceType: string }>> = {
  small: { name: 'Small', players: 'Up to 4 players', instanceType: 'c7g.large' },
  medium: { name: 'Medium', players: 'Up to 8 players', instanceType: 'm7g.large' },
  large: { name: 'Large', players: '12 or more players', instanceType: 'c8g.xlarge' },
};

export const sizeOf = (server: { size?: ServerSize }): ServerSize => server.size ?? DEFAULT_SERVER_SIZE;

/** The instance type a server runs on: an admin's override (performance tests), else its size's. */
export function instanceTypeFor(server: { instanceType?: string; size?: ServerSize }): string {
  return server.instanceType ?? SIZE_SPECS[sizeOf(server)].instanceType;
}

/** View and simulation distance in chunks: the game's own range (Minecraft's default is 10 for both). */
export const VIEW_DISTANCE_MIN = 3;
export const VIEW_DISTANCE_MAX = 32;

/** The longest server name, in characters. */
export const SERVER_NAME_MAX = 40;

/**
 * A server name: 1 to 40 characters, no control characters, and no spaces at either end (so
 * "Friday " and "Friday" can't both exist looking the same).
 */
export const SERVER_NAME = /^[^\s\p{Cc}](?:[^\p{Cc}]*[^\s\p{Cc}])?$/u;

/** What a server is called: its name, or for one created before names, "Server" and the end of its ID. */
export function serverName(server: { serverId: string; name?: string }): string {
  return server.name ?? `Server ${server.serverId.slice(-6)}`;
}

/** The longest name part of a handle, so a whole address stays well inside DNS's 63-character label. */
const HANDLE_NAME_MAX = 30;

/**
 * A server's handle: its name in lowercase letters, digits and hyphens (accents dropped; at most 30
 * characters; "server" if nothing is left), then the last 6 characters of its ID, which are random,
 * so it's unique without asking. Made once, at create, and never changed: it names the server's
 * address (`<handle>.<env's domain>`) and its page, so renaming the server mustn't break either.
 */
export function serverHandle(name: string, serverId: string): string {
  const part = name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, HANDLE_NAME_MAX)
    .replace(/^-+|-+$/g, '');
  return `${part || 'server'}-${serverId.slice(-6).toLowerCase()}`;
}

/** A server's stored handle, or (a server from before the backfill) one made from its name as it is now. */
export function handleOf(server: { serverId: string; name?: string; handle?: string }): string {
  return server.handle ?? serverHandle(serverName(server), server.serverId);
}

/** What `serverHandle` makes: never `www`, `auth`, `dev` or `stage`, as it always ends in `-` and 6 characters. */
export const SERVER_HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*-[a-z0-9]{6}$/;

/** GSI on `Servers` keyed by `instanceId` */
export const SERVERS_BY_INSTANCE_INDEX = 'byInstance';

/** GSI on `Servers`: `status` + `statusChangedAt`, for finding failed or stuck servers. */
export const SERVERS_BY_STATUS_INDEX = 'byStatus';

/** An item in the `Servers` table. Fields are added as milestones need them. */
export interface ServerRecord {
  /** ULID; the table's partition key. */
  serverId: string;
  /** What people call it (`SERVER_NAME`); servers created before names have none (`serverName`). */
  name?: string;
  /** `serverHandle`, made at create; servers from before got theirs from a one-off backfill. */
  handle?: string;
  ownerId: string;
  game: GameId;
  region: string;
  status: ServerStatus;
  version: string; // game version
  autoUpdate: boolean;
  agentChannel?: AgentChannel; // which agent releases this server follows; default stable
  idleStopMinutes?: number; // stop after this long with nobody playing; default DEFAULT_IDLE_STOP_MINUTES, 0 = never
  size?: ServerSize; // SIZE_SPECS: its instance, from its next wake; default DEFAULT_SERVER_SIZE
  // Admins only, for performance tests (M10.6); unset = the defaults.
  instanceType?: string; // INSTANCE_TYPE; overrides the size's
  viewDistance?: number; // chunks; default the game's own
  simulationDistance?: number; // chunks around players that keep running; default the game's own
  onlineMode?: boolean; // false: players aren't checked against their accounts (bots); dev only
  instanceId?: string;
  volumeId?: string;
  // Last report from the agent. `status` is owned by the lifecycle workflows; these are the agent's view.
  agentState?: AgentState;
  agentVersion?: string;
  agentReportedAt?: string; // ISO 8601 UTC
  agentMessage?: string;
  playersOnline?: number; // how many are on, the agent's latest count; cleared when the game stops
  lastPlayedAt?: string; // ISO 8601 UTC; when the agent last saw anyone playing
  // What EC2 last said about the instance, recorded by state sync.
  instanceState?: InstanceState;
  instanceStateAt?: string; // ISO 8601 UTC, the event's time
  publicIp?: string; // only while running; changes on every start
  lastStartedAt?: string;
  lastWokenBy?: string; // who last asked it to wake (a user's sub); on each run's usage row
  lastStoppedAt?: string;
  createdAt?: string; // ISO 8601 UTC
  destroyedAt?: string; // ISO 8601 UTC; set when the destroy workflow finishes
  archivedAt?: string; // ISO 8601 UTC; when it was last stored (M12: instance and disks gone, its newest backup kept). Never shown to users
  statusChangedAt?: string; // ISO 8601 UTC; set by every status change
  lastOperationId?: string; // the latest create/start/stop claim; names its workflow execution
  // Written by the lifecycle workflows.
  statusMessage?: string; // why the server is FAILED
  lastStopClean?: boolean; // the agent reported a clean stop (game saved) during the last stop
  stopReason?: string; // why the last stop happened, when not asked for (e.g. "no players for 30 minutes")
  // The newest backup, recorded when the agent finishes uploading it.
  lastBackupKey?: string;
  lastBackupAt?: string; // ISO 8601 UTC
  lastBackupBytes?: number;
  // A requested restore: this archive replaces the game data on the next start.
  restoreKey?: string;
  restoreSource?: 'upload'; // restoreKey is in the uploads bucket (an accepted upload); unset = the backups bucket
  restoreRequestedAt?: string; // ISO 8601 UTC
  // Its one invite link (M10.10): the code of its current invite, which may have expired since.
  inviteCode?: string;
}
